/**
 * Der Einladungslink im Browser — beim Host.
 *
 * **Sein Klartext existiert genau einmal**, in der Antwort auf die Partieerstellung. Gespeichert
 * ist serverseitig nur sein Hash (`CLAUDE.md`, Security by Design); ein verlorener Link ist nicht
 * wiederherstellbar, sondern nur **ersetzbar** — `POST /games/:id/invite-token` erzeugt einen
 * neuen, der alte verfällt dabei. Ohne diesen Speicher kostet also jedes Neuladen der Host-Seite
 * genau den Link, den der Host gerade verschicken wollte.
 *
 * **Die Partie-ID wird mitgespeichert.** Ein Host-Link legt nach einer beendeten Partie die
 * nächste an (`CLAUDE.md`, Berechtigungsmodell) — ein Token ohne Zuordnung würde Mitspieler in
 * eine Lobby schicken, die niemand mehr bedient. Das ist der Normalfall beim Testen und kein
 * Randfall.
 *
 * **Einer, nicht viele** — dieselbe Entscheidung wie bei der Sitzung (`session.ts`): ein Browser
 * bedient eine Partie. Wer eine zweite anlegt, ersetzt damit die erste.
 *
 * **`localStorage` und nicht `sessionStorage`:** dieser Token *soll* geteilt werden, und der Host
 * braucht ihn über ein Neuladen hinweg. Der Admin-Schlüssel liegt aus dem umgekehrten Grund im
 * `sessionStorage` (`admin/key.ts`).
 */

/** Der Schlüssel im `localStorage`. Mit Namensraum, und ein anderer als der der Sitzung. */
export const INVITE_KEY = 'flaghex.invite';

interface StoredInvite {
  readonly gameId: string;
  readonly token: string;
}

/**
 * Der gespeicherte Einladungslink **dieser** Partie, oder `undefined`.
 *
 * Ein Eintrag für eine andere Partie zählt als keiner: er gehört zu einer Lobby, die der Host
 * nicht mehr bedient.
 */
export function readInvite(gameId: string): string | undefined {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(INVITE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) {
    return undefined;
  }
  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    // Beschädigt — etwa von Hand bearbeitet oder halb geschrieben. Die Host-Seite bietet dann
    // einen neuen Link an, statt unbenutzbar zu werden.
    return undefined;
  }
  if (typeof stored !== 'object' || stored === null) {
    return undefined;
  }
  const entry = stored as Partial<StoredInvite>;
  if (entry.gameId !== gameId || typeof entry.token !== 'string' || entry.token === '') {
    return undefined;
  }
  return entry.token;
}

/**
 * Speichert den Link dieser Partie und vergisst dabei einen früheren.
 *
 * Ein leerer Token ist ein **Fehler im Programm** und wird laut abgewiesen: still gespeichert
 * ergäbe er einen Link, der auf nichts zeigt, und das fiele erst dem Mitspieler auf.
 */
export function writeInvite(gameId: string, token: string): void {
  const trimmed = token.trim();
  if (trimmed === '') {
    throw new Error('Ein leerer Einladungs-Token ist kein Link');
  }
  try {
    window.localStorage.setItem(INVITE_KEY, JSON.stringify({ gameId, token: trimmed }));
  } catch {
    // Absichtlich still: ein blockierter Speicher kostet Bequemlichkeit, keine Partie.
  }
}

/** Vergisst den Link — nach einem Partieende oder beim Erzeugen eines neuen. */
export function clearInvite(): void {
  try {
    window.localStorage.removeItem(INVITE_KEY);
  } catch {
    // Absichtlich still.
  }
}

/**
 * Der fertige Link, wie er weitergegeben wird.
 *
 * **Der Token steht im Fragment, nicht im Pfad** (`ARCHITECTURE.md` **E26**): ein Pfad landet im
 * Zugriffslog von nginx und im `Referer`, ein Fragment schickt der Browser nie an den Server.
 * Genau das macht diesen Link teilbar, ohne eine Spur zu hinterlassen.
 */
export function hostLink(origin: string, inviteToken: string): string {
  return `${origin}/#/join/${inviteToken}`;
}
